import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { DocmanFileRest } from './docman.service.file';
import { UserService } from '../../../core/services/core.user.service';

@Injectable()
export class DocmanFileModel {

    constructor(
        private docmanFileRest: DocmanFileRest,
        private userService: UserService
    ) {}

    private apiToModel(file: any) {
        file.isLocked = file.isLocked === 'Y';
        if (!_.isEmpty(this.userService.session.getUser())) {
            file.isMonitoring = !_.isEmpty(_.find(_.get(file, 'rel.monitoring', []), { id: this.userService.session.getUserId() }));
        } else {
            file.isMonitoring = false;
        }
        return file;
    }

    private modelToApi(file: any) {
        const _file: any = {
            id: file.id,
            description: file.description,
            isLocked: file.isLocked ? 'Y' : 'N',
        };

        if (file.rel) {
            _file.rel = _.cloneDeep(file.rel);
        }
        return _file;
    }

    private docFileVersionModelToApi(fileVersion: any) {
        return {
            id: fileVersion.id,
            changes: fileVersion.changes,
            docmanFile: fileVersion.docmanFile,
        };
    }

    /**
     * Get file details including versions, folders, monitoring, and downloads.
     * @param fileId - ID of the file to retrieve details.
     */
    getFileDetails(fileId: number): Promise<any> {
        return this.docmanFileRest.getFileDetails(fileId)
            .then(file => {
                if (!_.isEmpty(file.rel.versions)) {
                    _.sortBy(file.rel.versions, 'versionNumber');
                    file.currentVersion = _.maxBy(file.rel.versions, (o: any) => o.versionNumber);
                }

                file = this.apiToModel(file);
                return file;
            });
    }

    /**
     * Delete a specific file version.
     * @param fileVersionId - ID of the file version to delete.
     */
    deleteFileVersion(fileVersionId: number): Promise<any> {
        return this.docmanFileRest.deleteFileVersion(fileVersionId);
    }

    /**
     * Delete a file.
     * @param fileId - ID of the file to delete.
     */
    deleteFile(fileId: number): Promise<any> {
        return this.docmanFileRest.deleteFile(fileId);
    }

    /**
     * Save file settings.
     * @param file - File object to save.
     */
    saveFileSettings(file: any): Promise<any> {
        return this.docmanFileRest.saveFileSettings(file.id, this.modelToApi(file));
    }

    /**
     * Save a new file version.
     * @param fileVersion - File version object to save.
     */
    saveFileVersion(fileVersion: any): Promise<any> {
        return this.docmanFileRest.saveFileVersion(fileVersion.id, this.docFileVersionModelToApi(fileVersion));
    }

    /**
     * Restore a specific file version.
     * @param fileVersion - File version object to restore.
     */
    restoreFileVersion(fileVersion: any): Promise<any> {
        return this.docmanFileRest.restoreFileVersion(fileVersion.id);
    }

    /**
     * Rename a file.
     * @param file - File object to rename.
     */
    renameFile(file: any): Promise<any> {
        const _file = {
            id: file.id,
            section: file.section,
            fileName: file.fileName,
        };
        return this.docmanFileRest.renameFile(file.id, _file);
    }

    /**
     * Get assigned folders recursively for a folder.
     * @param folders - Array of folders.
     * @param folder - Folder object to assign.
     */
    getAssignedFolders(folders: any[], folder: any): any[] {
        if (folder.isSet) {
            if (!_.find(folders, { id: folder.id })) {
                folders.push({ id: folder.id });
            }
        } else {
            _.remove(folders, { id: folder.id });
        }

        _.each(folder.nodes, (node: any) => {
            folders = this.getAssignedFolders(folders, node);
        });

        return folders;
    }

    /**
     * Unzip a file into a folder.
     * @param fileVersionId - ID of the file version to unzip.
     * @param folderId - ID of the folder where the file will be unzipped.
     */
    unzipFile(fileVersionId: number, folderId: number): Promise<any> {
        return this.docmanFileRest.unzipFile(fileVersionId, folderId);
    }
}
